/** @jsx jsx */

import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  Fragment as _,
} from 'react';
import { Editor } from 'slate-react';
import { Value } from 'slate';
import { css, jsx } from '@emotion/core';
import { css as css_ } from 'emotion';
import EditMarker from 'components/UI/EditMarker';
import { connect } from 'react-redux';
import renderMark from './renderMark';
import renderNode from './renderNode';
import renderInline from './renderInline';
import html from './serializer';
import getBlockById from 'services/apiHelpers/getBlockById';
import setBlock from 'services/apiHelpers/setBlockProps';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import { Text } from '@chameleoncreator/creator-ui';
import getStore from 'services/getStore';
import { isKeyHotkey } from 'is-hotkey';
import ReadOnlyText from './ReadOnlyText';
import bulletStyles from './bulletStyles';
//
import { State } from 'store/store';
import { useSelector } from 'react-redux';

const store = getStore();

const isBoldHotkey = isKeyHotkey('mod+b');
const isItalicHotkey = isKeyHotkey('mod+i');
const isUnderlinedHotkey = isKeyHotkey('mod+u');

export const placeholderText = 'Add content...';

let delayUpdate;

const EditableText = ({ focusedNodeId, ...props }) => {
  const { id, className } = props;
  const block = getBlockById(id) as any;
  const {
    content = '',
    json,
    borderRadius = 0,
    useBackgroundControls,
    backgroundColor,
    bulletColor = undefined,
    //'#000',
    liTextColor = undefined,
    //'#000',
    enableSecondaryLinkColors,
    enableCustomLinkColors,
    customLinkColors = {},
  } = block?.props;

  const linkColors = useSelector((state: State) => {
    return {
      ...state.theme.links,
      // ...block.props.linkColors,
      ...(enableSecondaryLinkColors && {
        ...state.theme.linksSecondary,
      }),
      ...(enableCustomLinkColors && {
        // ...state.theme.linksSecondary,
        ...customLinkColors,
      }),
    };
  });

  const { link, visited, hover, focus, active } = linkColors;

  // console.log(linkColors);

  const [state, setState] = useState({
    value: html.deserialize(content),
  });

  const text = useRef<HTMLElement>(null);
  const editor = useRef<any>(null);
  const clearedFormatting = useRef<boolean>(false);
  const selected = focusedNodeId === id;

  if (window.dev() && selected)
    window['__CURRENT_EDITOR_CLEAR_ALL_FORMATTING__'] = (content, id) => {
      setBlock(getBlockById(id), {
        content,
      });

      setState({
        value: html.deserialize(content),
      });
    };

  useEffect(() => {
    if (!selected) return;

    parent.editor = editor.current;
  }, [selected]);

  useEffect(() => {
    if (content) {
      // console.log('has content');
      clearedFormatting.current = false;
    }
  }, [content]);

  useEffect(() => {
    if (!text.current) {
      return;
    }

    // remove all classes being links
    text.current.classList.forEach(className => {
      if (className.match('-links')) {
        text.current?.classList.remove(className);
      }
    });

    const links = css_(`
    // a {
    //   transition: all 0.1s;
    // }

    a:link {
      color: ${link} !important;
    }

    a:visited {
      color: ${visited} !important;
    }

    a:hover {
      color: ${hover} !important;
    }

    a:focus-visible {
      color: ${focus} !important;

      outline-offset: 2px !important;
      outline: 1px solid ${focus} !important;
      text-decoration: none !important;
    }

    a:active {
      color: ${active} !important;
    }
`);

    text.current.classList.add(links);
  }, [selected, link, visited, hover, focus, active]);

  useLayoutEffect(() => {
    //
    if (!text.current) {
      return;
    }

    //
    const innerText = text.current.innerText
      .toString()
      .replace(/\s\s+/g, ' ')
      .trim();

    const hasPlaceholder = innerText === placeholderText;

    text.current.style.textAlign = '';

    if (hasPlaceholder) {
      requestAnimationFrame(() => {
        if (text.current) {
          text.current.style.textAlign = 'left';
        }
      });
    }
  });

  const onChange = ({ value }) => {
    if (value.document !== state.value.document) {
      clearTimeout(delayUpdate);
      delayUpdate = setTimeout(() => {
        setBlock(getBlockById(id), {
          content: html.serialize(value),
        });
      }, 500);
    }

    setState({ value });
  };

  const onKeyDown = (e, editor, next) => {
    let mark;

    if (isBoldHotkey(e)) {
      mark = 'bold';
    } else if (isItalicHotkey(e)) {
      mark = 'italic';
    } else if (isUnderlinedHotkey(e)) {
      mark = 'underline';
    } else {
      return next();
    }

    e.preventDefault();
    editor.toggleMark(mark);
  };

  if (selected && editor.current) parent.editor = editor.current;

  if (!window.dev()) {
    return (
      <ReadOnlyText
        content={content}
        {...{
          className,
          useBackgroundControls,
          borderRadius,
          backgroundColor,
          bulletColor,
          liTextColor,
          linkColors,
        }}
      />
    );
  }

  return (
    <Text
      ref={text}
      id={`chameleon-text-${id}`}
      className={`chameleon-text${className ? ` ${className}` : ''}`}
      as="div"
      css={css`
			position: relative;
      width: 100%;
			padding: 12.5px 10px;
			${!selected && 'background: rgba(161, 188, 211, 0.15);'}
			box-shadow: ${selected ? '0 0 0 1px #ff0063' : 'none'};
		
      transition: background 0.1s, box-shadow 0.2s;

			&:hover {
				cursor: text;
				${!selected && 'box-shadow: 0 0 0 1px lightGrey;'}
				background: transparent;
      }
		
			* {
        pointer-events: ${selected ? 'auto' : 'none'};
      }

      ${bulletStyles(bulletColor, liTextColor)}

  
  
  `}
      style={
        useBackgroundControls || backgroundColor
          ? {
              borderRadius,
              background: backgroundColor,
              padding: `${12.5 + borderRadius}px ${10 + borderRadius}px`,
            }
          : {}
      }
      onClick={e => {
        e.stopPropagation();

        if (selected) return;
        setCurrentBlock({ id });
        setTimeout(() => {
          editor.current!.focus();
        }, 10);
      }}
      fontFamily={store.getState().theme.fontFamily || ''}
    >
      <Editor
        key={id}
        id={id}
        ref={editor}
        value={state.value}
        placeholder={'Add content...'}
        // schema={schema}
        readOnly={!window.dev()}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onPaste={e => {
          let pasted = (
            e.clipboardData || (window as any).clipboardData
          ).getData('text');

          // break it
          const pastedToPs = pasted
            .split(/(\r\n|\n|\r)/g)
            .map(p => p.replace(/\s\s+/g, ' ').trim())
            .filter(p => p !== '')
            .map(p => `<p data-type="paragraph">${p}</p>`)
            .join('');

          const { document } = html.deserialize(pastedToPs);

          editor.current!.insertFragment(document);

          e.preventDefault();
        }}
        renderMark={renderMark}
        // renderNode
        renderBlock={renderNode}
        renderInline={renderInline}
      />

      {selected && parent.updateEditor && parent.updateEditor(Math.random())}

      {!selected && <EditMarker />}
    </Text>
  );
};

export default connect(
  ({ focusedNodeId }: { focusedNodeId: string | number }) => ({
    focusedNodeId,
    [Math.random()]: '',
  })
)(EditableText);
